import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, FileText, FileSpreadsheet, Filter, X } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import CustomerDetailDrawer from "@/pages/admin/CustomerDetailDrawer";

export default function CustomersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const emptyFilter = { city: "", account_type: "", from_date: "", to_date: "" };
  const [filter, setFilter] = useState(emptyFilter);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get("/admin/customers", { params });
      setRows(res.data);
    } catch (err) {
      toast.error("Failed to load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const download = async (fmt) => {
    try {
      const token = localStorage.getItem("vc_token");
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await fetch(`${API}/admin/export/customers.${fmt}?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `vayucheck_customers.${fmt}`; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const clearFilter = () => { setFilter(emptyFilter); setTimeout(load, 0); };

  const stats = {
    total: rows.length,
    with_bookings: rows.filter((r) => r.booking_count > 0).length,
    commercial: rows.filter((r) => r.account_type === "commercial").length,
  };

  return (
    <div className="mt-6" data-testid="customers-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary mb-1">User directory</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Registered users</h2>
          <p className="text-sm text-muted-foreground mt-1">{stats.total} total · {stats.with_bookings} active · {stats.commercial} commercial</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="dl-customers-xlsx" onClick={() => download("xlsx")} variant="outline" className="rounded-lg border-border h-10">
            <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" /> Excel
          </Button>
          <Button data-testid="dl-customers-pdf" onClick={() => download("pdf")} variant="outline" className="rounded-lg border-border h-10">
            <FileText className="w-4 h-4 mr-2 text-red-600" /> PDF
          </Button>
        </div>
      </div>

      {/* Filter row */}
      <div className="border border-border bg-card rounded-2xl p-4 mb-6" data-testid="customer-filters">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">City</Label>
            <Input data-testid="cust-filter-city" value={filter.city} onChange={(e) => setFilter({ ...filter, city: e.target.value })} placeholder="e.g. Bengaluru" className="rounded-lg bg-secondary border-border h-10 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Account type</Label>
            <Select value={filter.account_type || "all"} onValueChange={(v) => setFilter({ ...filter, account_type: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="cust-filter-type" className="rounded-lg bg-secondary border-border h-10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">From date</Label>
            <Input data-testid="cust-filter-from" type="date" value={filter.from_date} onChange={(e) => setFilter({ ...filter, from_date: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">To date</Label>
            <Input data-testid="cust-filter-to" type="date" value={filter.to_date} onChange={(e) => setFilter({ ...filter, to_date: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" />
          </div>
          <div className="flex items-end gap-2">
            <Button data-testid="cust-filter-apply" onClick={load} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-10 flex-1">Apply</Button>
            <Button data-testid="cust-filter-clear" onClick={clearFilter} variant="outline" className="rounded-lg border-border h-10"><X className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-12 text-center" data-testid="empty-customers">
          <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-display text-xl font-bold mb-1">No users match</h3>
          <p className="text-sm text-muted-foreground">Try clearing filters or wait for the first sign-ups.</p>
        </div>
      ) : (
        <div className="border border-border bg-card rounded-2xl overflow-hidden">
          <Table data-testid="customers-table">
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-xs uppercase tracking-[0.15em]">User</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em]">Contact</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em]">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em]">Bookings</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em]">Last booking</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em]">Registered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((u) => (
                <TableRow key={u.user_id} data-testid={`customer-row-${u.user_id}`} onClick={() => setSelectedUserId(u.user_id)} className="border-border cursor-pointer hover:bg-secondary/50 transition-colors">
                  <TableCell>
                    <div className="font-medium">{u.name || "—"}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{u.auth_provider || "jwt"}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{u.email}</div>
                    {u.phone && <div className="text-xs text-muted-foreground">{u.phone}</div>}
                  </TableCell>
                  <TableCell>
                    <Badge className={`rounded-lg ${u.account_type === "commercial" ? "bg-primary/10 text-primary border-primary/30" : "bg-secondary text-foreground border-border"}`}>
                      {u.account_type || "individual"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-mono">{u.booking_count}</div>
                    {u.last_booking_city && <div className="text-[10px] text-muted-foreground">{u.last_booking_city}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.last_booking_at ? u.last_booking_at.slice(0, 10) : "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(u.created_at || "").slice(0, 10)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CustomerDetailDrawer
        userId={selectedUserId}
        open={!!selectedUserId}
        onOpenChange={(o) => { if (!o) setSelectedUserId(null); }}
      />
    </div>
  );
}
